import React from "react";
import { useAppStore } from "../../../../appStore";
import {
  AccessibleGroup,
  getAccessibleGroups,
} from "../../WireGuardTunnels/wireGuardGroupUtils";
import { resolveSiteTunnels } from "./siteTunnel";
import { siteFamily } from "./siteFamily";
import { useTroubleshootingStore } from "../troubleshootingStore/troubleshootingStore";
import {
  CameraLiveness,
  ConnectionKind,
  Finding,
  GapResult,
  ParentAccount,
  AlarmReceiver,
  ScopeMode,
  SiteConnectionDetail,
  SiteToSiteConnection,
  TroubleshootingSite,
  TroubleshootingTab,
  WireGuardTunnel,
} from "../troubleshootingInterfaces";

/**
 * Sites inside a scope: everything, one parent group, or one site.
 *
 * Pure so the hook below and the findings effect in `useTroubleshootingData`
 * share one definition — they used to each carry their own, and the copy in
 * the effect silently ignored group scope.
 */
export const scopedSitesFor = (
  scope: ScopeMode,
  groupId: number | null,
  siteId: number | null,
  sites: TroubleshootingSite[]
): TroubleshootingSite[] => {
  if (scope === "site") return sites.filter((site) => site.id === siteId);
  if (scope === "group")
    return sites.filter((site) => site.groupId === groupId);
  return sites;
};

/** Tunnels inside a scope; see `scopedSitesFor` for why this is pure. */
export const scopedTunnelsFor = (
  scope: ScopeMode,
  groupId: number | null,
  siteDetail: SiteConnectionDetail | null,
  tunnels: WireGuardTunnel[],
  groups: AccessibleGroup[] = getAccessibleGroups(),
  cameraHosts: (string | null | undefined)[] = []
): WireGuardTunnel[] => {
  if (scope === "all") return tunnels;
  if (scope === "group")
    return tunnels.filter((tunnel) =>
      isUnderGroup(tunnel.group ?? null, groupId, groups)
    );
  // One site: the tunnel whose subnet holds an address the connector dials,
  // never `siteDetail.wireguard`, which is the gateway (see siteTunnel.ts).
  return resolveSiteTunnels(siteDetail, cameraHosts, tunnels).tunnels;
};

/**
 * The findings that belong to the scope: every one at "all", otherwise those
 * naming no site or at least one site in scope. Shared by the Diagnosis list
 * and the copied report so the report says what the screen says (QA DG-35).
 */
export const findingsInScope = (
  findings: Finding[],
  scope: ScopeMode,
  scopedSiteIds: Iterable<number>
): Finding[] => {
  const ids = new Set(scopedSiteIds);
  return findings.filter(
    (f) =>
      scope === "all" ||
      f.affectedSiteIds.length === 0 ||
      f.affectedSiteIds.some((id) => ids.has(id))
  );
};

/**
 * Receivers that serve at least one site in scope — what "Send test alarm to
 * all" and the per-site "Send test alarm to receivers" action should hit.
 * Testing every receiver the user can see when one site is chosen would
 * page monitoring stations that have nothing to do with that site.
 */
export const receiverIdsInScope = (
  receivers: AlarmReceiver[],
  scope: ScopeMode,
  scopedSiteIds: Iterable<number>
): string[] => {
  if (scope === "all") return receivers.map((receiver) => receiver.id);
  const ids = new Set(scopedSiteIds);
  return receivers
    .filter((receiver) => receiver.site_ids.some((id) => ids.has(id)))
    .map((receiver) => receiver.id);
};

/**
 * Tabs that ignore the scope bar. None today: Connections takes an optional
 * site since ENG-2058 (picking one fills its addresses and labels its
 * tunnels; with none it works account-wide as before). The WireGuard
 * Debugger picks its own tunnel and hides the bar itself (see
 * `Troubleshooting.tsx`).
 */
export const UNSCOPED_TABS: TroubleshootingTab[] = [];

/** The scope this tab actually applies: the scope bar's, or "all" on an unscoped tab. */
export const useTabScope = (): ScopeMode => {
  const { tab, scope } = useTroubleshootingStore();
  return UNSCOPED_TABS.includes(tab) ? "all" : scope;
};

export const useScopedSites = (): TroubleshootingSite[] => {
  const { groupId, siteId, sites } = useTroubleshootingStore();
  const scope = useTabScope();
  return React.useMemo(
    () => scopedSitesFor(scope, groupId, siteId, sites),
    [scope, groupId, siteId, sites]
  );
};

/**
 * Per-list index of the cached group tree: rows by id, and each group's
 * ancestor chain once it has been walked. Keyed on the array itself, so it
 * lives exactly as long as that snapshot of `parentGroups` does.
 *
 * Without it every `isUnderGroup` call rebuilt a Map of the whole tree. A
 * superuser sees thousands of groups, and the pickers ask the question for
 * every account × tunnel and every site × tunnel — tens of millions of Map
 * inserts on each tab switch and each minute's refresh, several seconds on the
 * main thread, which is what Chrome's "Page Unresponsive" was about.
 */
interface GroupIndex {
  byId: Map<number, AccessibleGroup>;
  ancestors: Map<number, number[]>;
}

const GROUP_INDEXES = new WeakMap<AccessibleGroup[], GroupIndex>();

const indexFor = (groups: AccessibleGroup[]): GroupIndex => {
  let index = GROUP_INDEXES.get(groups);
  if (!index) {
    index = {
      byId: new Map(groups.map((group) => [group.id, group])),
      ancestors: new Map(),
    };
    GROUP_INDEXES.set(groups, index);
  }
  return index;
};

/**
 * A group and its ancestors, nearest first, from the cached group tree. Stops
 * at a parent the cache does not have, and on a cycle in cached rows. The
 * chain is computed once per group per list; callers must not mutate it.
 */
export const ancestorsOrSelf = (
  groupId: number | null,
  groups: AccessibleGroup[]
): number[] => {
  if (groupId == null) return [];
  const index = indexFor(groups);
  const cached = index.ancestors.get(groupId);
  if (cached) return cached;
  const chain: number[] = [];
  const seen = new Set<number>();
  let current: number | null = groupId;
  while (current != null && !seen.has(current)) {
    seen.add(current);
    chain.push(current);
    current = index.byId.get(current)?.parent ?? null;
  }
  index.ancestors.set(groupId, chain);
  return chain;
};

/**
 * Whether `groupId` is `underId` or sits somewhere beneath it. Tunnels and
 * network paths carry a group that is often a subgroup, while the scope bar
 * and the site list speak in the Alerts page's parent-account groups; this
 * is how the two meet. A group the cache does not know matches only itself.
 */
export const isUnderGroup = (
  groupId: number | null,
  underId: number | null,
  groups: AccessibleGroup[]
): boolean =>
  underId != null && ancestorsOrSelf(groupId, groups).includes(underId);

/**
 * Site ids that ride a WireGuard tunnel, on positive evidence only.
 *
 * Exact where `network_path/` gives a `wireguard` row with `customer_ids`
 * (ENG-1674, admin #2953), plus the selected site when its own
 * `customer/{id}/about/` names a WireGuard gateway. A tunnel with no such row
 * claims no site: a tunnel names a group, never a site, and offering every
 * site under that group listed sites that do not use WireGuard (QA SC-15).
 */
export const wireGuardSiteIdsFor = (
  paths: SiteToSiteConnection[],
  siteDetail: SiteConnectionDetail | null,
  gatewaySiteIds: number[] | null = null
): Set<number> => {
  // Sites with a WireGuard gateway, from customer/names?uses_wireguard (QA
  // SC-16): the same evidence the scope note uses for "connects via WireGuard".
  const ids = new Set<number>(gatewaySiteIds ?? []);
  paths
    .filter((path) => path.kind === "wireguard" && path.site_ids_exact)
    .forEach((path) => path.site_ids.forEach((id) => ids.add(id)));
  if (siteDetail?.wireguard != null) ids.add(siteDetail.id);
  return ids;
};

export const useWireGuardSiteIds = (): Set<number> => {
  const { siteDetail, siteToSite, wireGuardGatewaySiteIds } =
    useTroubleshootingStore();
  return React.useMemo(
    () =>
      wireGuardSiteIdsFor(
        siteToSite?.status === "ok" ? siteToSite.data : [],
        siteDetail,
        wireGuardGatewaySiteIds
      ),
    [siteDetail, siteToSite, wireGuardGatewaySiteIds]
  );
};

/**
 * The sites a tab's picker should offer.
 *
 * Each tab answers a different question, so offering a site it cannot say
 * anything about is a dead end: picking an SMTP site on the WireGuard tab only
 * ever renders "no tunnel here". Diagnosis and Alarm Receivers span everything.
 *
 * Which sites are SMTP sites is exact (`customer/names/?group_type=smtp`), so
 * they are removed from the other two lists. Which sites ride a tunnel is
 * exact only where `network_path/` carries `customer_ids` (see
 * useWireGuardSiteIds); until every tunnel has a path row Connections keeps
 * WireGuard sites: that tab already summarises a WireGuard site and links
 * across to the WireGuard tab, and dropping a whole account's sites on a
 * guess would leave it empty.
 */
export const useTabSiteOptions = (
  tab: TroubleshootingTab
): TroubleshootingSite[] => {
  const { sites, smtpSiteIds } = useTroubleshootingStore();
  const wireGuardIds = useWireGuardSiteIds();
  return React.useMemo(() => {
    const smtp = new Set(smtpSiteIds);
    if (tab === "smtp-cameras")
      return sites.filter((site) => smtp.has(site.id));
    if (tab === "wireguard")
      return sites.filter(
        (site) => wireGuardIds.has(site.id) && !smtp.has(site.id)
      );
    if (tab === "connections")
      return sites.filter((site) => !smtp.has(site.id));
    return sites;
  }, [tab, sites, smtpSiteIds, wireGuardIds]);
};

/**
 * Groups the tab's group picker offers — the Alerts page's parent-account
 * list, narrowed to what the tab can say something about: the WireGuard tab
 * to groups with a tunnel somewhere beneath them, the SMTP tab to groups
 * with an SMTP site beneath them (`group/?group_type=smtp`, as the Clips
 * page lists them). While the SMTP group list has not loaded, every group is
 * offered rather than none.
 */
export const useTabParentAccounts = (
  tab: TroubleshootingTab
): ParentAccount[] => {
  const { parentAccounts, tunnels, smtpAccountIds } = useTroubleshootingStore();
  return React.useMemo(() => {
    const groups = getAccessibleGroups();
    if (tab === "wireguard")
      return parentAccounts.filter((account) =>
        tunnels.some((tunnel) =>
          isUnderGroup(tunnel.group ?? null, account.id, groups)
        )
      );
    if (tab === "smtp-cameras" && smtpAccountIds)
      return parentAccounts.filter((account) =>
        smtpAccountIds.some((id) => isUnderGroup(id, account.id, groups))
      );
    return parentAccounts;
  }, [tab, parentAccounts, tunnels, smtpAccountIds]);
};

/**
 * Tunnels inside the current scope. With one site selected only that site's
 * tunnel (from `customer/{id}/about/`.wireguard) counts; a site with no tunnel
 * yields none.
 */
export const useScopedTunnels = (): WireGuardTunnel[] => {
  const { groupId, siteDetail, tunnels, cameraLiveness, siteCameras } =
    useTroubleshootingStore();
  const scope = useTabScope();
  return React.useMemo(
    () =>
      scopedTunnelsFor(
        scope,
        groupId,
        siteDetail,
        tunnels,
        getAccessibleGroups(),
        siteCameraHosts(cameraLiveness, siteCameras)
      ),
    [scope, groupId, siteDetail, tunnels, cameraLiveness, siteCameras]
  );
};

/**
 * Sites and tunnels for the scope bar's selection, whatever tab is open.
 * The verdict and the copied report describe what the user picked; the
 * tab-scoped hooks widen to "all" on the toolbox tabs, which is right for
 * those tables but would put every tunnel in the account into a report
 * about one site.
 */
export const useSelectionScope = (): {
  sites: TroubleshootingSite[];
  tunnels: WireGuardTunnel[];
} => {
  const {
    scope,
    groupId,
    siteId,
    sites,
    siteDetail,
    tunnels,
    cameraLiveness,
    siteCameras,
  } = useTroubleshootingStore();
  return React.useMemo(
    () => ({
      sites: scopedSitesFor(scope, groupId, siteId, sites),
      tunnels: scopedTunnelsFor(
        scope,
        groupId,
        siteDetail,
        tunnels,
        getAccessibleGroups(),
        siteCameraHosts(cameraLiveness, siteCameras)
      ),
    }),
    [
      scope,
      groupId,
      siteId,
      sites,
      siteDetail,
      tunnels,
      cameraLiveness,
      siteCameras,
    ]
  );
};

/**
 * Every address we know the site's connector dials: the liveness rows'
 * hosts where that endpoint exists, plus each camera's saved address from
 * the camera list (which prod has today).
 */
export const siteCameraHosts = (
  cameraLiveness: GapResult<CameraLiveness> | null,
  siteCameras: { ip?: string | null }[] | null
): string[] =>
  Array.from(
    new Set(
      [
        ...(cameraLiveness?.status === "ok"
          ? cameraLiveness.data.cameras.map((camera) => camera.host)
          : []),
        ...(siteCameras ?? []).map((camera) => camera.ip),
      ]
        .map((host) => String(host ?? "").trim())
        .filter(Boolean)
    )
  );

export const useServerName = () => {
  const { wireGuardOptions } = useAppStore();
  return React.useCallback(
    (serverId: number | null) => {
      if (!serverId) return "Unassigned";
      const option = (wireGuardOptions as { id: number; name: string }[]).find(
        (o) => o.id === serverId
      );
      return option?.name || `Unknown server ${serverId}`;
    },
    [wireGuardOptions]
  );
};

/** How a single site connects, from the fields `customer/{id}/about/` already returns. */
export const connectionKindForSite = (
  detail: SiteConnectionDetail | null,
  smtpSiteIds: number[]
): ConnectionKind => {
  if (!detail) return "unknown";
  const family = siteFamily(detail.integration_type_name);
  if (family === "smtp-push" || smtpSiteIds.includes(detail.id)) return "smtp";
  if (family === "cloud-pull") return "cloud";
  if (family === "clips-push") return "unknown";
  // `wireguard` is the site's gateway: the connector is placed in that zone
  // and reaches the site through a tunnel there.
  if (detail.wireguard != null) return "wireguard";
  if (detail.server_ip || detail.management_server_ip) return "direct";
  return "unknown";
};

export const useScopeLabel = () => {
  const { scope, groupId, siteId, sites, parentAccounts } =
    useTroubleshootingStore();
  if (scope === "all") return "All sites";
  if (scope === "group")
    return (
      parentAccounts.find((account) => account.id === groupId)?.name ||
      "One group"
    );
  return sites.find((site) => site.id === siteId)?.name || "One site";
};

/**
 * Labels for the group picker. A superuser's parent-account list has dozens
 * of repeated names ("Demo Folder" ×6, "California" ×2 …); MUI keys rows by
 * label unless told otherwise, so repeats left ghost rows behind when the
 * list was filtered (ENG-1984). The picker keys by id now, and a repeated
 * name says where the group sits so the two can be told apart.
 */
export const parentAccountLabels = (
  accounts: ParentAccount[],
  groups: AccessibleGroup[] = getAccessibleGroups()
): Map<number, string> => {
  const seen = new Map<string, number>();
  accounts.forEach((account) =>
    seen.set(account.name, (seen.get(account.name) ?? 0) + 1)
  );
  const byId = new Map(groups.map((group) => [group.id, group]));
  const labels = new Map<number, string>();
  accounts.forEach((account) => {
    if ((seen.get(account.name) ?? 0) < 2) {
      labels.set(account.id, account.name);
      return;
    }
    const parentId = byId.get(account.id)?.parent ?? null;
    const parent = parentId == null ? undefined : byId.get(parentId);
    labels.set(
      account.id,
      parent
        ? `${account.name} · under ${parent.name}`
        : `${account.name} · #${account.id}`
    );
  });
  return labels;
};
